import { Close } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { getFacilityReport, rejectFacilityReport } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import RichtextEditor from '../../../common/RichtextEditor';

export default function RejectResult({
  open,
  title,
  onClose,
  facilityId,
  reportQuarter,
  reportYear,
}) {
  const [report, setReport] = useState();
  const [reportLoading, setReportLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState();

  // console.log('report', report);

  useEffect(() => {
    fetchReport();
  }, []);

  const handleReject = async () => {
    setSaving(true);

    try {
      const receipient = {
        emailAddress: report?.submittedByEmail,
        fullName: report?.submittedBy,
      };

      const payload = {
        reportID: report?.reportId,
        //emailMessage: '',
        comment: message,
        recipients: [],
      };

      // console.log(payload)
      const res = await rejectFacilityReport(payload);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        openNotification({ type: 'success', message: status?.desc });
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      const errMsg = error?.data?.result?.status?.desc;
      openNotification({
        type: 'error',
        message: !errMsg ? 'Something went wrong. Please try again' : errMsg,
      });
    } finally {
      setSaving(false);
    }
  };

  const fetchReport = async () => {
    setReportLoading(true);
    try {
      const res = await getFacilityReport(facilityId, reportQuarter, reportYear);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setReport(data);
      }
    } catch (error) {
    } finally {
      setReportLoading(false);
    }
  };

  const handleRenderContent = () => {
    if (reportLoading) return <LoaderSkeleton />;
    if (!reportLoading && !report) return null;
    if (!reportLoading && report) return renderContent();
  };

  const renderContent = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Alert Section */}
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 600 }}>Confirm Rejection</AlertTitle>
          <Typography variant="body2" gutterBottom>
            You are about to reject the submission of{' '}
            <strong>{report?.reportType}</strong> results for{' '}
            <strong>{report?.facility}</strong>.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>
            <em>
              Recommendation: Please add a remark so the submitter can address the issues and resubmit.
            </em>
          </Typography>
        </Alert>

        {/* Editor Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box>
            <Typography variant="subtitle2" color="text.primary" fontWeight={600}>
              Add a remark for the submitter
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Submitted by <strong>{report?.submittedBy}</strong>.
            </Typography>
          </Box>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden', // Keeps the rich text editor neatly inside the rounded corners
              transition: 'border-color 0.2s',
              '&:hover': {
                borderColor: 'text.disabled',
              },
            }}
          >
            <RichtextEditor value={message} onChange={(content) => setMessage(content)} />
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 } // Softens the entire dialog box
      }}
    >
      {/* Dialog Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 3,
          pb: 1,
        }}
      >
        <DialogTitle variant="h6" fontWeight={700} sx={{ p: 0 }}>
          {title || 'Reject Result'}
        </DialogTitle>
        <IconButton 
          onClick={onClose} 
          disabled={saving}
          sx={{ color: 'text.secondary' }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Dialog Content */}
      <DialogContent sx={{ px: 3, py: 2 }}>
        {handleRenderContent()}
      </DialogContent>

      {/* Dialog Footer */}
      <DialogActions 
        sx={{ 
          px: 3, 
          py: 2.5, 
          borderTop: 1, 
          borderColor: 'divider',
          gap: 1 
        }}
      >
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          disabled={saving}
          onClick={onClose}
          sx={{ 
            textTransform: 'none', 
            borderRadius: 2,
            px: 3,
            borderColor: 'divider' 
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="error"
          disableElevation
          disabled={saving}
          startIcon={saving && <CircularProgress size={16} color="inherit" />}
          sx={{ 
            textTransform: 'none', 
            borderRadius: 2,
            px: 4
          }}
          onClick={handleReject}
        >
          {saving ? 'Rejecting...' : 'Reject'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}